<template>
  <div class="partEdit" v-if="part && currentProject._id">
    <!-- Part information / deleted -->
    <div v-if="!partExists(part._id)" class="partwarning">
      <b-icon icon="calendar-x" />
      {{ $t('The part does not exist') }}
    </div>

    <!-- Part information / existing -->
    <div v-else>
      <!-- Edit fields wrapper / kept as is with new padding style -->
      <div class="partContainer">
        <!-- Part intro -->
        <div class="partIntro bo-padding-std">
          <router-link
            :to="{
              name: 'ProjectPlanEdit',
              params: { _id: currentProject._id },
              query: $route.query,
            }"
          >
            <button class="modal-close-button">{{ $t('Close') }} <b-icon icon="x" /></button>
          </router-link>
          <div class="titles">
            <div v-if="parent" class="parent">{{ parent }}</div>
            <input
              :value="part.title"
              type="text"
              :readonly="!editingAllowed || !hasPropertyPermissions('Part', 'title')"
              class="partTitle"
              @change="updateSimpleProperty('title', $event.target.value)"
            />
          </div>
          <debug :object="part" />

          <div v-if="part.archived" class="partwarning">
            <b-icon icon="calendar-x" />
            {{ $t('The part has been deleted') }}
          </div>
          <div v-else-if="!partInRange" class="partwarning">
            &larr;
            {{ $t('The part is scheduled outside of the shown date range, and thus not shown in the projectplan') }}
          </div>
        </div>
        <!-- end: partIntro -->

        <!-- a wrapper to be able to make bordered boxes (in alignment with the rest) -->
        <div class="accordion-wrapper" style="margin-top: 2 0px">
          <part-edit-accordion
            v-model="activeTab"
            tab="showDates"
            :title="$t('Execution')"
            icon="clock"
            :icon-props="{ scale: '0.9' }"
          >
            <template #title-info>
              <span>
                {{ $t('w.{week}', { week: weekOf(startdate) }) }}
                {{ $t(dayOfWeek(startdate)) }}
                <span v-if="weekOf(startdate) != weekOf(part.expectedDonedate)">
                  - <br />{{ $t('w.{week}', { week: weekOf(part.expectedDonedate) }) }}&nbsp;{{
                    $t(dayOfWeek(part.expectedDonedate))
                  }}
                </span>
                <span v-else-if="dayOfWeek(startdate) != dayOfWeek(part.expectedDonedate)">
                  - {{ $t(dayOfWeek(part.expectedDonedate)) }}
                </span>
              </span>
            </template>

            <div class="flex-between align-center">
              <span class="date-label">{{ $t('Start Date') }}</span>
              <DatePicker
                :placeholder="$t('Start Date')"
                :value="startdate"
                :monday-first="true"
                :typeable="true"
                :format="iso_format"
                :inline="true"
                :calendarweek="true"
                @input="updateSimpleDateProperty('startdate', $event)"
                :readonly="!draggingAllowed"
                :popup="true"
              />
            </div>
            <div class="flex-between align-center pt-2">
              <span class="date-label">{{ $t('End Date') }}</span>
              <DatePicker
                :value="part.expectedDonedate"
                :monday-first="true"
                :format="iso_format"
                :typeable="true"
                :inline="false"
                @input="updateSimpleDateProperty('expectedDonedate', $event)"
                :readonly="!draggingAllowed"
                :popup="true"
              />
            </div>
            <div class="flex-between align-center pt-2">
              <span class="date-label"><b-icon icon="bell-fill" /> {{ $t('Notify') }}</span>
              <DatePicker
                :value="part.controldate"
                :monday-first="true"
                :format="iso_format"
                :typeable="true"
                :inline="false"
                @input="updateSimpleDateProperty('controldate', $event)"
                :readonly="!hasPropertyPermissions('Part', 'controldate')"
                :popup="true"
              />
            </div>
          </part-edit-accordion>

          <part-edit-accordion
            v-model="activeTab"
            tab="showGroup"
            :title="$t('Responsible')"
            icon="house-fill"
            :icon-props="{ scale: '0.9' }"
          >
            <template #title-info>
              <span v-if="selectedPartManager._id">
                {{ personCompanyOrPersonName(selectedPartManager._id) }}
              </span>
              <span v-else-if="part.partOccupation">
                {{ $t(part.partOccupation) }}
              </span>
              <span v-else class="">
                {{ $t(inheritedOccupation) }}
              </span>
            </template>
            <b-labelled :title="$t('Responsible Manager')">
              <multiselect
                id="partManagerId"
                :value="getPerson(part.partManagerId)"
                :custom-label="(p) => personName(p._id)"
                :disabled="!hasPropertyPermissions('Part', 'partManagerId')"
                :multiple="false"
                :options="managingPersons"
                track-by="_id"
                :placeholder="personName(managerId)"
                class="boon-multiselect"
                @input="updateSimpleProperty('partManagerId', $event ? $event._id : '')"
              >
                <div slot="noResult">
                  {{ $t('No matching person found') }}
                </div>
              </multiselect>
            </b-labelled>

            <b-labelled :title="$t('Area of expertise')" v-if="hasPropertyPermissions('Part', 'partOccupation')">
              <multiselect
                id="partOccupation"
                :value="part.partOccupation"
                :custom-label="(p) => $t(p)"
                :disabled="!hasPropertyPermissions('Part', 'partOccupation')"
                :multiple="false"
                :options="currentProjectRoles"
                :placeholder="$t(inheritedOccupation)"
                class="boon-multiselect"
                @input="updateSimpleProperty('partOccupation', $event)"
              >
                <div slot="noResult">
                  {{ $t('No matching expertise found') }}
                </div>
              </multiselect>
            </b-labelled>

            <b-labelled
              v-if="!isOnlySubContractor"
              :title="isDefaultSubContractor ? $t('Subcontractor (Assigned from project)') : $t('Subcontractor')"
            >
              <multiselect
                id="partSubcontractorId"
                :value="selectedPartManager"
                :custom-label="(p) => personCompanyName(p._id)"
                :placeholder="defaultSubContractor"
                :disabled="!hasPropertyPermissions('Part', 'partSubcontractorId')"
                :multiple="false"
                :options="allPersonsCompanyList"
                track-by="_id"
                group-label="companyName"
                group-values="persons"
                class="boon-multiselect"
                @open="allPersonsCompanyList = groupedPersons()"
                @input="updateSimpleProperty('partSubcontractorId', $event ? $event._id : '')"
              >
                <div slot="noResult">
                  {{ $t('No matching person found') }}
                </div>
              </multiselect>
            </b-labelled>
          </part-edit-accordion>

          <part-edit-accordion
            v-model="activeTab"
            tab="showDelivery"
            :title="$t('Delivery')"
            icon="truck"
            :icon-props="{ scale: '0.9' }"
          >
            <template #title-info>
              <span v-if="deliverydate">
                <span v-if="!deliveryFulfilled">
                  <strong>
                    {{ timeSummary(deliverydate) }}
                  </strong>
                </span>
                <span v-else>
                  <strong>{{ $t('Fulfilled at') }}</strong>
                  {{ timeSummary(deliverydate) }}
                  <!--{{ personCompanyName(part.deliveryFulfilledBy) }}-->
                </span>
              </span>
            </template>

            <!-- delivery -->
            <div class="delivery-wrapper">
              <div v-if="isDeliveryFulfilled" class="delivery-fulfilled-wrapper">
                <div class="delivery-fulfilled-info">
                  <span class="delivery-fulfilled-header">{{ $t('Delivery is fulfilled') }}</span>
                  <span class="delivery-fulfilled-undo">
                    <a href="#" class="text-small" @click="setDeliveryFulfilled(false)">
                      <b-icon icon="arrow-countrerclockwise" /> {{ $t('Undo') }}
                    </a>
                  </span>
                  <span class="delivery-fulfilled-details">
                    <span class="fulfilled-by-wrapper overflow-dots">
                      <span class="details-icon"><b-icon icon="person" /></span>
                      <span class="detail">
                        {{ personCompanyName(part.deliveryFulfilledBy) }}
                      </span>
                    </span>
                    <span class="fulfilled-at-wrapper">
                      <span class="details-icon"><b-icon icon="clock-history" /></span>
                      <span class="detail">
                        {{ momentItSince(part.deliveryFulfilledAt, 'YYYY-MM-DD HH:mm') }}
                      </span>
                    </span>
                  </span>
                </div>
              </div>
              <!-- end: delivery is fulfilled -->
              <!-- could be an else here for unfulfilled data -->
            </div>
            <!-- end: delivery -->

            <div style="text-align: right; margin-bottom: 3px">
              <a v-if="deliverydate && !isDeliveryFulfilled" @click="deleteDelivery">
                <b-icon icon="trash-fill" />
                <span class="text-small">
                  {{ $t('Remove') }}
                </span>
              </a>
              &nbsp;
            </div>
            <span class="inline Xtext-small">{{ $t('Delivery date') }}</span>
            <div style="float: right; margin-bottom: 20px">
              <DatePicker
                :value="deliverydate"
                :typeable="true"
                :inline="false"
                :popup="true"
                :monday_first="true"
                :format="iso_format"
                :week-numbers="true"
                :placeholder="$t('Set a date...')"
                :calendar-class="'datepicker-align-right'"
                :readonly="!hasPropertyPermissions('Part', 'deliverydate')"
                :disabled="isDeliveryFulfilled"
                @input="updateSimpleDateProperty('deliverydate', $event)"
              />
            </div>

            <!-- hide all subfields if no deliverydate is selected -->
            <div v-if="deliverydate">
              <br />
              <label for="deliveryRecipient" class="Xtext-small">{{ $t('Delivery recipient') }}</label>

              <multiselect
                class="boon-multiselect-new mt-1 delivery-recipient"
                :custom-label="(p) => personCompanyName(p._id)"
                :disabled="!hasPropertyPermissions('Part', 'deliverydate') || isDeliveryFulfilled"
                :multiple="false"
                :options="allPersonsCompanyList"
                :placeholder="$t('Select delivery recipient (optional)')"
                :value="getPerson(part.deliveryRecipient)"
                @input="updateSimpleProperty('deliveryRecipient', $event ? $event._id : '')"
                group-label="companyName"
                group-values="persons"
                id="deliveryRecipient"
                track-by="_id"
                @open="allPersonsCompanyList = groupedPersons()"
              >
                <div slot="noResult">
                  {{ $t('No matching person found') }}
                </div>
              </multiselect>

              <div class="mt-3" style="line-height: 2em">
                <div>
                  <input
                    id="deliveryLull"
                    type="checkbox"
                    :disabled="!hasPropertyPermissions('Part', 'deliveryFulfilled') || isDeliveryFulfilled"
                    v-model="checkbox_deliveryLull"
                  />
                  <label for="deliveryLull">{{ $t('Requires Lull') }}</label>
                </div>

                <div>
                  <input
                    id="deliveryCarrying"
                    type="checkbox"
                    :disabled="!hasPropertyPermissions('Part', 'deliveryFulfilled') || isDeliveryFulfilled"
                    v-model="checkbox_deliveryCarrying"
                  />
                  <label for="deliveryCarrying">{{ $t('Needs carrying') }}</label>
                </div>

                <div class="flex-between">
                  <div>
                    <input
                      id="deliveryTime"
                      type="checkbox"
                      :disabled="!hasPropertyPermissions('Part', 'deliveryFulfilled') || isDeliveryFulfilled"
                      v-model="checkbox_deliveryTime"
                    />
                    <label for="deliveryTime">{{ $t('Unloading time') }}</label>
                  </div>

                  <div class="minute-input-wrapper" v-if="checkbox_deliveryTime">
                    <input
                      type="text"
                      :disabled="!hasPropertyPermissions('Part', 'deliveryFulfilled') || isDeliveryFulfilled"
                      :value="part.deliveryTimeMinutes"
                      :placeholder="15"
                      @change="updateSimpleProperty('deliveryTimeMinutes', $event.target.value)"
                    />
                    <span class="suffix">{{ $t('min') }}</span>
                  </div>
                </div>
              </div>

              <!-- delivery size -->
              <div class="mt-3">
                <div for="deliveryText" class="Xtext-small">{{ $t('Delivery size') }}</div>
                <input
                  class="boon-default mt-1"
                  type="text"
                  :disabled="!hasPropertyPermissions('Part', 'deliveryFulfilled') || isDeliveryFulfilled"
                  :value="part.deliverySizeText"
                  :placeholder="$t('Specify no. of boxes/pallets')"
                  @change="updateSimpleProperty('deliverySizeText', $event.target.value)"
                />
              </div>

              <!-- delivery instructions -->
              <div class="mt-3">
                <span for="deliveryText" class="Xtext-small">{{ $t('Delivery instructions') }}</span>
                <textarea
                  class="mt-1"
                  rows=""
                  ref="deliveryText"
                  @change="updateSimpleProperty('deliveryText', $event.target.value)"
                  :readonly="!editingAllowed || !hasPropertyPermissions('Part', 'deliveryText') || isDeliveryFulfilled"
                  :value="part.deliveryText"
                />
              </div>

              <div class="mt-3">
                <button
                  v-if="isDeliveryFulfillable"
                  id="fulfillDeliveryButton"
                  class="btn btn-green btn-wide"
                  @click="setDeliveryFulfilled(true)"
                >
                  {{ $t('Set Delivery fulfilled') }}
                </button>
              </div>
            </div>
          </part-edit-accordion>

          <part-edit-accordion
            v-model="activeTab"
            tab="showTodoItems"
            :title="$t('Todo')"
            icon="list"
            :icon-props="{ scale: '0.9' }"
          >
            <template #title-info>
              <span v-if="selectedPartTodoItemsRemaining">
                <b>{{ selectedPartTodoItemsRemaining }}</b> items remaining.
              </span>
            </template>

            <span v-if="selectedPartTodoItems.length === 0" class="info" v-html="$t('todo-list-instructions')"> </span>
            <TodoItemList :part="part" />
          </part-edit-accordion>

          <part-edit-accordion
            v-model="activeTab"
            tab="inspection"
            :title="selectedPartInspection ? selectedPartInspection.title : $t('Create Activity')"
            icon="calendar-event"
          >
            <template #title-info>
              <span v-if="selectedPartInspection">
                {{ timeSummary(selectedPartInspection.beginTime) }}
              </span>
            </template>

            <div>
              <PartEditInspection :part="part" />
            </div>
          </part-edit-accordion>

          <part-edit-accordion
            v-model="activeTab"
            tab="showChat"
            :title="$t('Comment')"
            icon="chat-left-dots-fill"
            :icon-props="{ scale: '0.9' }"
          >
            <template #title-info>
              <div v-if="firstPartComment !== false" style="line-height: 1em; margin-top: 0.2em">
                <!-- {{ firstPartComment }} -->
                <b>{{ personName(firstPartComment.authorPersonId) }}</b>
                <span class="inline acc-small">{{ momentItSince(firstPartComment.commentdate) }}</span>
              </div>
              <div v-else>
                {{ $t('No comments yet.') }}
              </div>
            </template>

            <div class="msger-chat">
              <div
                class="msg"
                v-for="partComment in partComments"
                :key="partComment._id"
                :class="{ 'right-msg': partComment.authorPersonId == loggedInPerson._id }"
              >
                <div class="msg-bubble">
                  <div class="msg-info">
                    <div class="msg-info-name">{{ personName(partComment.authorPersonId) }}</div>
                    <div class="msg-info-time">{{ chatTime(partComment.commentdate) }}</div>
                  </div>
                  <div class="msg-text" v-html="partComment.text"></div>
                </div>
              </div>
            </div>
            <!-- msgr-chat -->

            <div class="grey-divider" :class="{ 'mt-0': !partComments.length }"></div>

            <div v-if="hasSimplePermissions('addPartComment')" class="comments-wrapper">
              <div class="comments-new-intro" v-html="$t('comments-instructions')"></div>
              <div class="msger-inputarea">
                <At
                  scrollRef=".editChild"
                  :members="projectPersons"
                  :tabSelect="true"
                  nameKey="firstName"
                  v-model="newComment"
                  class="msger-input"
                >
                  <span slot="embeddedItem" slot-scope="item">
                    <span :data-id="item.current._id" class="tag"
                      >{{ item.current.firstName }} {{ item.current.lastName }}</span
                    >
                  </span>
                  <template slot="item" slot-scope="row">
                    <span>{{ row.item.firstName }} {{ row.item.lastName }}</span>
                  </template>
                  <div class="editor add-comment" contenteditable></div>
                </At>
                <button @click="addComment" :disabled="newComment.trim() === ''" class="btn-green">
                  {{ $t('Send') }}
                </button>
              </div>
            </div>
          </part-edit-accordion>

          <!--
            Erik 2022-07-05: Did not yet decide what to do with "work hours"
            part-edit-accordion
            :title="$t('Work Hours')"
            v-model="activeTab"
            tab="showWorkHours"
            icon="clock-history"
          >
            <template #title-info>
            </template>

            <b-labelled :title="$t('Work Hours')">
              <input
                id="workHours"
                type="text"
                pattern="[0-9]*"
                class="boon-default"
                :value="part.workHours"
                @change="updateSimpleProperty('workHours', $event.target.value)"
                :readonly="!editingAllowed || !hasPropertyPermissions('Part', 'workHours')"
              />
            </b-labelled>
          </!-->
        </div>
        <!-- END: accordion-wrapper -->
      </div>

      <!-- end partContainer -->

      <div v-if="showControlFulfilledButton" style="text-align: center; padding: 10px">
        <button v-if="!controlFulfilled" class="btn button-bordered" @click="toggleFinished">
          {{ $t('Control fulfilled') }}
        </button>
        <button v-if="controlFulfilled" class="btn button-bordered" @click="confirmUndoFinished">
          {{ $t('Revert approval') }}
        </button>
      </div>
      <SimpleConfirm ref="confirm" />
    </div>
  </div>
</template>
<script>
import { EventBus } from '@/common/eventbus';
import { mapActions, mapGetters } from 'vuex';
import { recentFuzzytime, fuzzytime } from '@/common/timeHelpers';
import * as Roles from '@/store/roles';
import * as treeHelpers from '@/common/treeHelpers';
import At from 'vue-at/dist/vue-at';
import BLabelled from '@/components/toolbar/BLabelled';
import DatePicker from '@/components/datepicker/DatePicker';
import moment from 'moment';
import Multiselect from 'vue-multiselect';
import Toggler from '@/components/toolbar/Toggler';
import TodoItemList from '@/components/projectplan/TodoItemList';
import { prepareCommentForDisplay } from '@/common/htmlHelper';
import SimpleConfirm from '@/components/simpleConfirm/SimpleConfirm';
import PartEditAccordion from './PartEditAccordion.vue';
import PartEditInspection from './PartEditInspection.vue';

export default {
  name: 'PartEdit',
  metaInfo() {
    return {
      title: this.part.title,
    };
  },
  props: ['filterDate', 'filterDateEnd', 'part'],
  data: function () {
    return {
      allPersonsCompanyList: [{ _id: false }],

      iso_format: 'yyyy-MM-dd', // Required for datepicker to work. Move to glob func.

      // Active tab. Init with default tab.
      activeTab: 'info',

      newComment: '',
    };
  },
  methods: {
    ...mapActions(['addPartComment', 'savePartProperties', 'selectPart']),
    timeSummary(timestamp) {
      const m = moment(timestamp);
      return this.$t('{year} w.{week} {dayOfWeek} {time}', {
        week: m.isoWeek(),
        dayOfWeek: this.$t(m.format('dddd')),
        time: m.hour() > 0 ? m.format('HH:mm') : '',
        year: m.year() !== moment().year() ? m.year() : '',
      });
    },
    convertLinks: function (text) {
      return prepareCommentForDisplay(text);
    },
    commentdate: function (date) {
      return this.$t(recentFuzzytime(date));
    },
    addComment: function () {
      this.addPartComment({
        part: this.part,
        project: this.currentProject,
        text: this.newComment,
      });
      this.newComment = '';
    },
    openTab: function (el) {
      if (this.activeTab === el) {
        this.activeTab = false;
      } else {
        this.activeTab = el;
      }
    },
    findPart: function () {
      // Even if already selected, so parents are opened
      this.selectPart(this.part);
      // After its visible and rendered
      this.$nextTick(() => {
        EventBus.$emit('focusPart', this.part);
      });
    },
    updateSimpleProperty: function (property, newValue) {
      if (this.part[property] !== newValue) {
        var newPartProperties = {};
        newPartProperties[property] = newValue;
        console.log(property, newValue, this.part, newPartProperties);
        this.savePartProperties({ updatePart: this.part, newPartProperties });
      }
    },

    /**
     * Add date validation before committing new value, for input text
     */
    updateSimpleDateProperty: function (property, newValue) {
      console.log('updateSimpleDateProperty');
      if (!moment(newValue).isValid()) {
        console.log('not valid');
        return; // Show error?
      }
      this.updateSimpleProperty(property, moment(newValue).format('YYYY-MM-DD'));
    },
    controlFulfilledSound: function (event) {
      var checked = event.target.checked;
      var audio = new Audio('/static/BOON_control_fulfilled.wav');

      if (checked === true) {
        audio.play();
        this.selectPart(null);
      }
    },
    weekOf: function (date) {
      return /* moment(date).year() + '-' + */ moment(date).isoWeek();
    },
    dayOfWeek: function (date) {
      return /* moment(date).year() + '-' + */ moment(date).format('dddd');
    },

    deleteDelivery: function () {
      this.updateSimpleProperty('deliverydate', '0000-01-01');
    },

    /* Set deliveryFulfilled and save time and user */
    async setDeliveryFulfilled(value) {
      console.log('Fulfilled', this.loggedInPerson._id);
      if (value === true) {
        this.savePartProperties({
          updatePart: this.part,
          newPartProperties: {
            deliveryFulfilled: true,
            deliveryFulfilledAt: moment().unix(),
            deliveryFulfilledBy: this.loggedInPerson._id,
          },
        });
      } else if (value === false) {
        if (
          await this.$refs.confirm.open(
            this.$t('Confirm'),
            this.$t('This will remove the delivery fulfillment. Do you want to proceed?'),
            { agreeText: this.$t('confirm-yes'), cancelText: this.$t('confirm-cancel') }
          )
        ) {
          this.savePartProperties({
            updatePart: this.part,
            newPartProperties: {
              deliveryFulfilled: false,
              deliveryFulfilledAt: false,
              deliveryFulfilledBy: false,
            },
          });
        }
      }
    },

    // Convert a unix timestamp to a moment formatted string - Here until tested!
    momentIt: function (timestamp, format) {
      format = format || 'YYYY-MM-DD';
      return moment.unix(timestamp).format(format);
    },

    // Since string - Here until tested!
    momentItSince: function (timestring) {
      return this.$t(fuzzytime(timestring));
    },

    chatTime: function (timestring, format) {
      if (moment().isSame(moment(timestring))) {
        format = format || 'HH:mm';
        return moment(timestring).format(format);
      }
      format = format || 'YYYY-MM-DD HH:mm';
      return moment(timestring).format(format);
    },

    toggleFinished() {
      console.log('Confirmed!');
      this.controlFulfilled = !this.controlFulfilled; // Just dirty for now - it's a test!
    },

    // Test Confirm dialogue
    async confirmUndoFinished() {
      if (
        await this.$refs.confirm.open('Confirm', this.$t('Do you want to change status into unfinished?'), {
          agreeText: this.$t('Yes, change status'),
          cancelText: this.$t('No, cancel'),
        })
      ) {
        this.toggleFinished();
      }
    },
  },
  computed: {
    ...mapGetters([
      'currentProject',
      'currentProjectRoles',
      'editingAllowed',
      'getPerson',
      'fetchStage',
      'findNearestParentProperty',
      'groupedPersons',
      'hasSimplePermissions',
      'hasRole',
      'hasPropertyPermissions',
      'managingPersons',
      'partExists',
      'personCompanyName',
      'personCompanyOrPersonName',
      'personName',
      'projectPersons',
      'projectDraggingAllowed',
      'selectedPartComments',
      'selectedPartInspection',
      'selectedPartTodoItems',
      'selectedPartTodoItemsRemaining',
      'loggedInPerson',
    ]),
    partComments: function () {
      return this.selectedPartComments;
    },
    // Return first comment if any
    firstPartComment: function () {
      return this.partComments[0] || false;
    },
    parent: function () {
      if (!this.part) {
        return '';
      }
      // console.log('PartEdit::parent', JSON.stringify(this.part));
      console.log('PartEdit::parent', this.part._id, this.currentProject._id);

      const parent = treeHelpers.findParent(this.currentProject, this.part._id);

      if (parent) {
        const stage = this.fetchStage(parent._id);
        return stage.title;
      }

      return '';
    },
    isOnlySubContractor: function () {
      return this.hasRole(Roles.SubContractor);
    },
    inheritedOccupation: function () {
      if (this.part.partOccupation) {
        return this.part.partOccupation;
      }

      var parent = treeHelpers.findParent(this.currentProject, this.part._id);
      var occupation;
      while (!occupation && parent && parent !== this.currentProject) {
        occupation = this.fetchStage(parent._id).stageOccupation;
        if (!occupation) {
          parent = treeHelpers.findParent(this.currentProject, parent._id);
        }
      }
      if (!occupation) {
        return '';
      }
      return occupation;
    },
    managerId: function () {
      if (this.part.partManagerId) {
        return this.part.partManagerId;
      }

      return this.findNearestParentProperty(
        this.currentProject,
        this.part._id,
        'stageManagerId',
        'developingManagerId'
      );
    },
    selectedPartManager: function () {
      if (this.part.partSubcontractorId !== '') {
        // console.log(JSON.stringify(this.getPerson(this.part.partSubcontractorId)));
        return this.getPerson(this.part.partSubcontractorId);
      }
      return '';
    },
    defaultSubContractor: function () {
      if (!this.inheritedOccupation) {
        return this.$t('Subcontractor not assigned');
      }

      var subcontractor = this.currentProject.projectSubcontractors.find(
        (p) => p.occupation === this.inheritedOccupation
      );
      if (!subcontractor) {
        subcontractor = this.currentProject.projectManagers.find((p) => p.occupation === this.inheritedOccupation);
      }

      if (!subcontractor || !subcontractor.personId) {
        return this.$t('Subcontractor for {occupation} not assigned', {
          occupation: this.$t(this.inheritedOccupation),
        });
      }
      // console.log(this.personCompanyName(subcontractor.personId));
      return this.personCompanyName(subcontractor.personId);
    },
    isDefaultSubContractor: function () {
      if (!this.part.partOccupation) {
        return false;
      }
      if (this.part.partSubcontractorId) {
        return false;
      }

      var subcontractor = this.currentProject.projectSubcontractors.find(
        (p) => p.occupation === this.part.partOccupation
      );
      if (!subcontractor) {
        subcontractor = this.currentProject.projectManagers.find((p) => p.occupation === this.part.partOccupation);
      }

      if (!subcontractor || !subcontractor.personId) {
        return false;
      }
      return true;
    },
    draggingAllowed: function () {
      if (!this.projectDraggingAllowed) {
        return false;
      }
      var parent = treeHelpers.findParent(this.currentProject, this.part._id);
      var locked = false;
      while (!locked && parent && parent !== this.currentProject) {
        locked = this.fetchStage(parent._id).lockedDragNDrop;
        if (!locked) {
          parent = treeHelpers.findParent(this.currentProject, parent._id);
        }
      }
      return !locked;
    },
    partInRange: function () {
      return moment(this.part.startdate).isBetween(this.filterDate, this.filterDateEnd, null, '[]');
    },
    controlIcon: function () {
      if (!this.part.controldate || this.part.controldate === '0000-01-01') {
        return 'eye-fill';
      }
      if (this.part.controlFulfilled !== true && moment(this.part.controldate).isBefore(moment())) {
        return 'hand-thumbs-down';
      }
      if (this.part.controlFulfilled) {
        return 'check';
      }
    },
    deliveryIcon: function () {
      if (!this.part.deliverydate || this.part.deliverydate === '0000-01-01') {
        return 'truck';
      }
      if (this.part.deliveryFulfilled !== true && moment(this.part.deliverydate).isBefore(moment())) {
        return 'hand-thumbs-down';
      }
      if (this.part.deliveryFulfilled) {
        return 'check';
      }
    },
    deliveryFulfilled: {
      get: function () {
        return this.part.deliveryFulfilled;
      },
      set: function (newValue) {
        this.savePartProperties({
          updatePart: this.part,
          newPartProperties: { deliveryFulfilled: newValue },
        });
      },
    },
    controlFulfilled: {
      get: function () {
        return this.part.controlFulfilled;
      },
      set: function (newValue) {
        this.savePartProperties({
          updatePart: this.part,
          newPartProperties: { controlFulfilled: newValue },
        });
      },
    },

    /**
     *  Can this part currently be Control Fulfilled ? (pre check)
     *
     *  Conditions:
     *    Not before startdate
     *    Does not contain unfullfilled delivery
     *    TODO: May not have uncompleted checkboxes
     */
    showControlFulfilledButton: function () {
      // Not before startdate   (removed by request @2021-03-23)
      // if (moment().isBefore(moment(this.part.startdate))) {
      //  return false;
      // }
      // No unfullfilled delivery
      if (!this.part.deliveryFulfilled && this.deliverydate) {
        return false;
      }
      // TODO: Checkboxes...

      // Passed tests.
      return true;
    },

    startdate: function () {
      var dat = moment(this.part.startdate);
      if (!dat.isValid()) {
        dat = moment();
      }
      return dat.format('YYYY-MM-DD');
    },
    deliverydate: function () {
      var dat = moment(this.part.deliverydate);
      if (!dat.isValid() || this.part.deliverydate === '0000-01-01') {
        return '';
      }
      return dat.format('YYYY-MM-DD');
    },
    deliveryStatus: function () {
      console.log(this.part.deliverydate);
      return this.part.deliverydate < new Date();
    },

    /* Delivery Fulfillable?   Delivery date is today or earlier AND not already fulfilled */
    isDeliveryFulfillable: function () {
      return moment(this.part.deliverydate).startOf('day') < moment() && !this.part.deliveryFulfilled;
    },

    isDeliveryFulfilled: function () {
      return this.part.deliveryFulfilled;
    },

    // And take care of the checkboxes.
    checkbox_deliveryLull: {
      get: function () {
        return this.part.deliveryLull;
      },
      set: function (value) {
        this.updateSimpleProperty('deliveryLull', value);
      },
    },

    checkbox_deliveryCarrying: {
      get: function () {
        return this.part.deliveryCarrying;
      },
      set: function (value) {
        this.updateSimpleProperty('deliveryCarrying', value);
      },
    },

    checkbox_deliveryTime: {
      get: function () {
        return this.part.deliveryTime;
      },
      set: function (value) {
        this.updateSimpleProperty('deliveryTime', value);
      },
    },

    checkbox_deliverySize: {
      get: function () {
        return this.part.deliverySize;
      },
      set: function (value) {
        this.updateSimpleProperty('deliverySize', value);
      },
    },
  },
  filters: {},
  components: {
    At,
    BLabelled,
    DatePicker,
    Multiselect,
    TodoItemList,
    Toggler,
    PartEditAccordion,
    PartEditInspection,
    SimpleConfirm,
  },
};
</script>

<style scoped>
.partEdit {
  display: flex;
  flex-direction: column;
  padding-top: 10px;
}
.partContainer {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
}
.bo-padding-std {
  padding-left: 15px;
  padding-right: 15px;
}
.flex-between {
  display: flex;
  justify-content: space-between;
}
.flex-between .dotted-overflow {
  min-width: 0;
  display: block;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.partwarning {
  background-color: #f97c7c;
  border-radius: 5px;
  padding: 0px 4px;
}
button {
  margin: 2px;
  display: inline-flex;
  align-self: flex-end;
  cursor: pointer;
}
.titles {
  padding-top: 10px;
  padding-bottom: 10px;
}
.parent:before {
  content: '> ';
}
.parent {
  font-size: 0.7em;
  font-weight: 600;
  color: #d67d07;
}
.partTitle {
  font-size: 1.5em;
  font-weight: 400;
  margin-bottom: 5px;
}
.partTitle:hover {
  outline: 1px solid #999;
}
.ctxContainer {
  display: flex;
  flex-flow: row wrap;
  justify-content: space-evenly;
  margin-top: 10px;
  padding: 10px 0;
  width: 100%;
  border-top: 1px solid #c9783d;
  border-bottom: 1px solid #c9783d;
  margin-bottom: 20px;
}
.ctxContainer i {
  font-size: 2em;
  transform: translateY(0%);
}
.commentContainer,
.controlDateContainer,
.deliveryContainer,
.groupContainer,
.workHoursContainer {
  width: 100%;
}
.title {
  text-align: center;
  display: block;
  font-size: 1.2em;
  cursor: pointer;
  padding: 5px 0;
}
.dateTitle {
  display: block;
  text-align: center;
  font-size: 0.8em;
}
.ctxHours {
  display: flex;
  flex-flow: column nowrap;
}
.ctxHours div {
  display: flex;
  flex-flow: row wrap;
  padding: 10px 0;
}
.ctxHours div > label,
.ctxHours div > input {
  width: 50%;
}
.ctxHours div > input {
  border-bottom: 1px solid;
}
.ctxHours div > .subTitle {
  width: 100%;
  font-size: 0.8em;
}
.checkboxContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 0;
}
.controlFulfilledContainer {
  margin-left: -10px;
  margin-right: -10px;
  width: auto;
  background: #1c2731;
  color: white;
  padding: 20px 10px;
  text-transform: uppercase;
}
.chatContainer {
  padding: 20px 0;
}
textarea {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 3px;
  margin-top: 5px;
  padding: 5px 10px;
}
textarea:focus,
textarea:focus {
  box-shadow: 0px 0px 0px 1px #666;
}
.atwho-wrap {
  display: inline-block;
  width: 75%;
}

.commentRow {
  background: white;
  margin-bottom: 8px;
}
.commentAuthor {
  display: block;
  width: 100%;
  font-size: 50%;
  text-align: right;
}
/* Transition */

/* Accordion */
.accordion-wrapper {
  margin-top: 0em;
  padding-bottom: 10px;
}
/* .accordion-toggler {
    position: relative;
    cursor: pointer;
    border-top: 1px solid #999;
    padding: 0.8em 0.8em;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all 0.2s ease-in-out;
    background: #f2f2f2;
  }
  .accordion-toggler.last:not(.active) {
    border-bottom: 1px solid #999;
  }
  .accordion-toggler .acc-title {
    color: #000;
    font-size: 1em;
  }
  .accordion-toggler .acc-title i {
    transition: all 0.4s ease-in-out;
    color: #999;
    font-size: 1em;
    margin-right: 0.2em;
  }
  .accordion-toggler .acc-toggle-icon:after {
    position: relative;
    top: 2px;
    font-family: "Material Icons";
    content: "keyboard_arrow_right";
    font-weight: bold;
    color: #666;
    display: inline-block;
    transition: all 0.4s ease;
  }
  .accordion-toggler:hover {
    background: #bababa;
  }
  .accordion-toggler.active {
    background: #d67d07;
    border-top: #d67d07;
    box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.2);
    z-index: 2;
  }
  .accordion-toggler.active .acc-title {
    color: #fff;
  }
  .accordion-toggler.active .acc-title i {
    color: #000;
  }
  .accordion-toggler.active .acc-toggle-icon:after {
    transform: rotate(90deg);
  }

  .bo-accordion {
    position: relative;
    border-top: 1px solid #f1f1f1;
    padding-top: 0.8em;
    padding-bottom: 0.8em;
    transition: all 0.2s ease-in-out;
    background: #fff;
  }
  .bo-accordion:last-of-type {
    padding-bottom: 15px;
    border-bottom: 1px solid #f1f1f1;
  }
  .bo-accordion .acc-header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    min-height: 48px;
  }
  .bo-accordion .acc-header .acc-icon {
    font-size: 1.4em;
    padding: 0px 15px 0px 8px;
    cursor: pointer;
    background: #000;
    width: 1.8em;
    min-width: 1.8em;
    height: 1.8em;
    border-radius: 50%;
    text-align: center;
    line-height: 1.7em;
    vertical-align: middle;
    padding: 0px 0px;
    color: #fff;
    margin-right: 12px;
    opacity: 0.6;
  }
  .bo-accordion .acc-header .acc-title-wrapper .acc-title {
    font-size: 1rem;
    color: #000;
  }
  .bo-accordion .acc-header .acc-title-wrapper .acc-subtitle {
    font-size: 1.2em;
    color: #333;
  }
  .bo-accordion .acc-header .acc-title-wrapper .acc-subtitle strong {
    font-weight: 600;
  }
  .bo-accordion .acc-header .acc-title-wrapper .acc-subtitle.acc-small, .bo-accordion .acc-header .acc-title-wrapper .acc-subtitle .acc-small {
    font-size: 0.8em;
  }
  .bo-accordion .acc-header .acc-title-wrapper .acc-subtitle.acc-big {
    font-size: 1rem;
  }
  .bo-accordion .acc-toggle {
    position: absolute;
    right: 8px;
    top: -4px;
    font-size: 0.8em;
    color: #82bcff;
    text-decoration: none;
    padding: 4px;
  }
  .bo-accordion .acc-toggle:hover {
    color: #0064e0;
  }
  .bo-accordion .acc-close {
    position: absolute;
    display: none;
    right: 8px;
    top: -4px;
    font-size: 0.8em;
    color: #333;
    text-decoration: none;
    color: #0064e0;
    padding: 4px;
  }
  .bo-accordion .acc-close:hover {
    color: #82bcff;
  }
  .bo-accordion .acc-body .acc-content {
    margin-top: 10px;
  }
  .bo-accordion:hover {
    background: #f0f4f9;
  }
  .bo-accordion.active {
    padding-bottom: 2em;
    background: #f0f4f9;
  }
  .bo-accordion.active .acc-header {
    justify-content: flex-start;
    align-items: center;
  }
  .bo-accordion.active .acc-header .acc-icon {
    font-size: 1em;
    line-height: 1.7em;
    display: flex;
    justify-content: center;
    align-items: center;

  }
  .bo-accordion.active .acc-header .acc-title-wrapper .acc-subtitle {
    display: none;
  }
  .bo-accordion.active .acc-toggle {
    display: none;
  }
  .bo-accordion.active .acc-close {
    display: block;
  } */

/* Standard button */
.button-dark {
  position: relative;
  background-color: #1c2731;
  border: none;
  color: #fff;
  padding: 0.5em 1em;
  width: auto;
  text-align: center;
  transition-duration: 0.4s;
  text-decoration: none;
  overflow: hidden;
  cursor: pointer;
}
.button-dark:after {
  content: '';
  background: #f1f1f1;
  display: block;
  position: absolute;
  padding-top: 300%;
  padding-left: 350%;
  margin-left: -20px !important;
  margin-top: -120%;
  opacity: 0;
  transition: all 0.8s;
}
.button-dark:active:after {
  padding: 0;
  margin: 0;
  opacity: 1;
  transition: 0s;
}
.button-bordered {
  background: #fff;
  color: #333;
  padding: 0.36em 1em;
  border: 0.14em solid #333;
  border-radius: 10em;
}
.button-bordered:hover {
  background: #eee;
}
/* Delivery Status */
.delivery-fulfilled-wrapper {
  margin-top: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ddd;
  /* NOT USED PLACEHOLDER */
}
.delivery-fulfilled-wrapper .delivery-fulfilled-details {
  display: block;
  font-size: 80%;
}
.delivery-fulfilled-wrapper .delivery-fulfilled-details .fulfilled-by-wrapper,
.delivery-fulfilled-wrapper .delivery-fulfilled-details .fulfilled-at-wrapper {
  display: block;
  padding-left: 10px;
}
.delivery-fulfilled-wrapper .delivery-fulfilled-extras .delivery-extras-date,
.delivery-fulfilled-wrapper .delivery-fulfilled-extras .delivery-extras-recipient {
  display: block;
}
.delivery-fulfilled-wrapper .delivery-fulfilled-extras .extras-header {
  font-weight: 500;
}
/* CHAT */
.msger-chat {
  flex: 1;
  overflow-y: auto;
}
.msger-chat .msg {
  display: flex;
  align-items: flex-end;
  margin-bottom: 10px;
}
.msger-chat .msg:last-of-type {
  margin: 0;
}
.msger-chat .msg-img {
  width: 40px;
  min-width: 40px;
  height: 40px;
  margin-right: 10px;
  background: #ddd;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  border-radius: 50%;
}
.msger-chat .msg-bubble {
  max-width: 250px;
  padding: 8px 15px;
  border-radius: 15px;
  background: #fff;
  border: 1px solid #dadada;
}
.msger-chat .msg-info {
  margin-bottom: 5px;
  font-size: 0.8em;
}
.msger-chat .msg-info-name {
  font-weight: bold;
}
.msger-chat .msg-info-time {
  font-size: 0.85em;
}
.msger-chat .left-msg .msg-bubble {
  border-bottom-left-radius: 0;
}
.msger-chat .right-msg {
  flex-direction: row-reverse;
}
.msger-chat .right-msg .msg-info-name {
  text-align: right;
}
.msger-chat .right-msg .msg-info-time {
  text-align: right;
}
.msger-chat .right-msg .msg-bubble {
  background: #4470a7;
  color: #fff;
  border-bottom-right-radius: 0;
  border: none;
}
.msger-chat .right-msg .msg-img {
  margin: 0 0 0 10px;
}
.msger-chat .msger-chat {
  background-color: #fcfcfe;
}
.comments-wrapper .comments-new-intro {
  font-size: 0.8em;
}
.msger-inputarea {
  display: flex;
  padding: 10px 0px;
  margin-top: 0px;
}
.msger-inputarea .msger-inputarea * {
  padding: 10px;
  border: none;
  border-radius: 3px;
  font-size: 1em;
}
.msger-inputarea .msger-input {
  flex: 1;
}
.msger-inputarea .editor {
  padding: 10px;
  border: 1px solid #3b3b3b;
  background: #fff;
}
.msger-inputarea button {
  padding: 10px 20px;
  margin: 0;
  margin-left: 10px;
  border: 1px solid transparent;
}
</style>

<style>
/* DatePicker override */
.vdp-datepicker input {
  border: 1px solid #00000050;
  padding: 2px 4px;
  width: 105px;
}

/* Offset input field for visual appearence */
.vdp-datepicker {
  left: -6px;
}

.input-inline-section {
  display: inline-block;
}

.showAsEditable {
  outline: 1px dotted #d67d0750;
}

.datepicker-align-right {
  right: 0px;
}
.datepicker-align-middle {
  left: -84px;
}

.float-right {
  float: right;
}

.text-status-panel {
  margin: 20px 0px;
}

.week-text {
  font-size: 70%;
}

.bo-py {
  padding-left: 15px;
  padding-right: 15px;
}

.text-danger {
  color: red;
}
.text-warning {
  color: #c9783d;
}
.text-inherited {
  color: #888;
  font-style: italic;
}
.text-small {
  font-size: 13px;
}

.inline {
  display: inline-block;
}

.align-center {
  align-items: center;
}

.pt-1 {
  padding: 2px 0px 0px 0px;
}
.py-1 {
  padding: 0px 2px;
}
.px-1 {
  padding: 2px 0px;
}

.pt-2 {
  padding: 4px 0px 0px 0px;
}
.py-2 {
  padding: 0px 4px;
}
.px-2 {
  padding: 4px 0px;
}

.pt-3 {
  padding: 4px 0px 0px 0px;
}
.py-3 {
  padding: 0px 6px;
}
.px-3 {
  padding: 6px 0px;
}

.border-top {
  border-top: 1px solid #dadada;
}

.btn {
  border-radius: 3px;
  margin: 0px 0px 0px 0px;
}

.btn-dark {
  color: black;
  border: 0px solid black;
  padding: 10px 20px;
}
.btn-dark:hover {
  color: white;
  background: black;
}

.btn-grey {
  color: #777;
  background: #ddd;
  border: 0px solid black;
  padding: 10px 20px;
}
.btn-grey:hover {
  color: white;
  background: grey;
}

.btn-green {
  color: #fff;
  background: #1da045;
  border: 0px solid black;
  padding: 10px 20px;
}
.btn-green:hover {
  color: white;
  background: #2db255;
}

.btn-wide {
  display: inline-block;
  width: 100%;
  text-align: center;
}

.overflow-dots {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

/* Move to GLOBAL??? */
input {
  max-width: 100%;
}
input.boon-default {
  display: inline-block;
  width: 100%;
  padding: 6px 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
input.boon-default:focus {
  box-shadow: 0px 0px 0px 1px #666;
}

.minute-input-wrapper {
  display: flex;
  flex-direction: row;
  width: 100px;
  border: 1px solid #dadada;
  border-radius: 4px;
}
.minute-input-wrapper input {
  display: inline-block;
  text-align: right;
  width: 100%;
  padding: 0px 6px;
  flex-grow: 1;
  border-radius: 4px 0px 0px 4px;
}
.minute-input-wrapper .suffix {
  display: inline-block;
  padding: 0px 6px;
  background: #dadada;
}

.grey-divider {
  padding-top: 15px;
  margin-top: 15px;
  border-top: 1px solid #dadada;
}

.delivery-recipient input {
  font-size: 0.75rem;
}
</style>



// WEBPACK FOOTER //
// src/components/projectplan/PartEdit.vue